import React, { useState, useEffect } from 'react';
import {
  Dialog,
  ScrollableDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/scrollable-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RoleBadge } from './RoleBadge';
import { TeamMember, UpdateMemberData } from '@/lib/api/services/teamService';
import { useAuth } from '@/components/AuthProvider';
import { useBusinessAccounts } from '@/hooks/useBusinessAccounts';
import { Building2, Phone, CheckCircle, AlertCircle } from 'lucide-react';

interface MinimalEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: TeamMember;
  onSave: (updatedUser: UpdateMemberData & { id: string }) => void;
}

export const MinimalEditDialog: React.FC<MinimalEditDialogProps> = ({
  open,
  onOpenChange,
  user,
  onSave
}) => {
  const parseAccessFlag = (value: unknown): boolean => {
    return value === true || value === 1 || value === '1' || value === 'true';
  };
  const getHasAllAccessFromPermissions = (permissions: unknown): boolean => {
    if (!permissions) return false;
    if (typeof permissions === 'string') {
      try {
        const parsed = JSON.parse(permissions);
        return parseAccessFlag(parsed?.has_all_conversations_access);
      } catch {
        return false;
      }
    }
    return parseAccessFlag((permissions as any)?.has_all_conversations_access);
  };

  const { user: currentUser } = useAuth();
  const roleRaw = (currentUser?.role || '').toString().toUpperCase();
  const isSuperAdmin = !!(currentUser as any)?.is_super_admin;
  const isAdmin = roleRaw === 'ADMIN' || isSuperAdmin;
  const [formData, setFormData] = useState(() => ({
    name: user.name,
    email: user.email,
    role: (user.role || 'USER').toString().toUpperCase(),
    status: (user as any).is_active ? 'active' : 'inactive',
  }));

  const { data: businessAccounts, isLoading: isLoadingBusinessAccounts } = useBusinessAccounts();
  const [selectedBusinessAccounts, setSelectedBusinessAccounts] = useState<string[]>([]);
  const [accountPermissions, setAccountPermissions] = useState<Record<string, { has_all_conversations_access: boolean }>>({});

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        role: (user.role || 'USER').toString().toUpperCase(),
        status: (user as any).is_active ? 'active' : 'inactive',
      });

      // Initialize assignments from user data
      if (user.assigned_accounts) {
        const ids = user.assigned_accounts.map(a => a.id);
        setSelectedBusinessAccounts(ids);

        const perms: Record<string, { has_all_conversations_access: boolean }> = {};
        user.assigned_accounts.forEach(a => {
          perms[a.id] = {
            has_all_conversations_access: getHasAllAccessFromPermissions(a.permissions)
          };
        });
        setAccountPermissions(perms);
      } else {
        // Fallback: auto-select if only one business account exists
        if (businessAccounts && businessAccounts.length === 1) {
          setSelectedBusinessAccounts([businessAccounts[0].id]);
        } else {
          setSelectedBusinessAccounts([]);
        }
        setAccountPermissions({});
      }
    }
  }, [user]);

  // Handle Role Change Effect for Assignments (similar to Invite)
  useEffect(() => {
    if (formData.role === 'ADMIN' && businessAccounts) {
      // Admins automatically get all accounts with all permissions
      const allIds = businessAccounts.map(ba => ba.id);
      // Only auto-select if we just switched to ADMIN from something else
      // But for Edit, we might want to preserve existing if already set?
      // Let's enforce the rule: Admin = All Access.

      // Check if we need to update state to avoid infinite loops
      const areAllSelected = allIds.length === selectedBusinessAccounts.length && allIds.every(id => selectedBusinessAccounts.includes(id));

      if (!areAllSelected) {
        setSelectedBusinessAccounts(allIds);
        const perms: Record<string, { has_all_conversations_access: boolean }> = {};
        allIds.forEach(id => {
          perms[id] = { has_all_conversations_access: true };
        });
        setAccountPermissions(perms);
      }
    }
  }, [formData.role, businessAccounts]);


  const isSingleAccount = !!(businessAccounts && businessAccounts.length === 1);

  const handleBusinessAccountToggle = (businessAccountId: string) => {
    // Prevent deselecting when it's the only business account
    if (isSingleAccount) return;
    setSelectedBusinessAccounts(prev =>
      prev.includes(businessAccountId)
        ? prev.filter(id => id !== businessAccountId)
        : [...prev, businessAccountId]
    );
  };

  const handleValidationToggle = (businessAccountId: string, checked: boolean) => {
    setAccountPermissions(prev => ({
      ...prev,
      [businessAccountId]: { ...prev[businessAccountId], has_all_conversations_access: checked }
    }));
  };

  const handleSelectAll = () => {
    if (businessAccounts) {
      setSelectedBusinessAccounts(businessAccounts.map(ba => ba.id));
    }
  };

  const handleDeselectAll = () => {
    setSelectedBusinessAccounts([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedRole = (formData.role || 'USER').toString().toUpperCase();

    // Construct assignments payload
    const assignments = selectedBusinessAccounts.map(accountId => ({
      business_account_id: accountId,
      permissions: {
        // For ADMIN/USER, force full access. For AGENT, use selection (default false)
        has_all_conversations_access: normalizedRole === 'AGENT'
          ? (accountPermissions[accountId]?.has_all_conversations_access || false)
          : true
      }
    }));
    const hasAllConversationsAccess = normalizedRole !== 'AGENT'
      ? true
      : assignments.some(a => a.permissions.has_all_conversations_access === true);

    onSave({
      id: user.id,
      name: formData.name,
      email: formData.email,
      role: normalizedRole as 'ADMIN' | 'AGENT' | 'USER',
      status: formData.status,
      assignments,
      hasAllConversationsAccess
    });
  };

  const dialogHeader = (
    <DialogHeader>
      <DialogTitle className="text-[20px] md:text-[22px] text-left font-semibold">Edit Team Member</DialogTitle>
      <DialogDescription className="text-left">
        Update member information
      </DialogDescription>
    </DialogHeader>
  );

  const dialogFooter = (
    <DialogFooter>
      <Button
        type="button"
        variant="outline"
        onClick={() => onOpenChange(false)}
      >
        Cancel
      </Button>
      <Button type="submit" form="edit-member-form">
        Save Changes
      </Button>
    </DialogFooter>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ScrollableDialogContent
        className="max-h-[95vh]"
        maxWidthXs="98vw"
        maxWidthSm="90vw"
        maxWidthMd="70vw"
        maxWidthLg="70vw"
        maxWidthXl="50vw"
        maxWidthXxl="30vw"
        header={dialogHeader}
        footer={dialogFooter}
      >
        <form id="edit-member-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Role</Label>
            <Select
              value={formData.role}
              onValueChange={(value) => setFormData(prev => ({ ...prev, role: value as 'ADMIN' | 'AGENT' | 'USER' }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USER">
                  <div className="flex items-center gap-2">
                    <RoleBadge role="USER" size="sm" showIcon={false} />
                    <span>User</span>
                  </div>
                </SelectItem>
                <SelectItem value="AGENT">
                  <div className="flex items-center gap-2">
                    <RoleBadge role="AGENT" size="sm" showIcon={false} />
                    <span>Agent</span>
                  </div>
                </SelectItem>
                {isAdmin && (
                  <SelectItem value="ADMIN">
                    <div className="flex items-center gap-2">
                      <RoleBadge role="ADMIN" size="sm" showIcon={false} />
                      <span>Admin</span>
                    </div>
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <span className="text-sm">Active</span>
              <Switch
                checked={formData.status === 'active'}
                onCheckedChange={(checked) =>
                  setFormData(prev => ({
                    ...prev,
                    status: checked ? 'active' : 'inactive'
                  }))
                }
              />
            </div>
          </div>

          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Business Accounts Access</Label>
              {!isSingleAccount && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                    disabled={isLoadingBusinessAccounts}
                  >
                    Select All
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDeselectAll}
                    disabled={isLoadingBusinessAccounts}
                  >
                    Deselect All
                  </Button>
                </div>
              )}
            </div>

            <div className="text-sm text-muted-foreground">
              Choose which business accounts this team member can access and configure permissions.
            </div>

            {isLoadingBusinessAccounts ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-muted-foreground">Loading business accounts...</span>
              </div>
            ) : businessAccounts && businessAccounts.length > 0 ? (
              <Card>
                <CardContent className="p-4 space-y-3">
                  {businessAccounts.map((businessAccount) => {
                    const isSelected = selectedBusinessAccounts.includes(businessAccount.id);
                    const hasAllAccess = accountPermissions[businessAccount.id]?.has_all_conversations_access || false;

                    return (
                      <div
                        key={businessAccount.id}
                        className={`flex flex-col p-3 rounded-lg border transition-colors ${isSelected
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                          }`}
                      >
                        {/* Account Selection Row */}
                        <div
                          className={`flex items-center justify-between w-full ${isSingleAccount ? 'cursor-default' : 'cursor-pointer'}`}
                          onClick={() => handleBusinessAccountToggle(businessAccount.id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <div className="font-medium">{businessAccount.name}</div>
                                <div className="text-sm text-muted-foreground flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {businessAccount.phone_number}
                                </div>
                              </div>
                            </div>
                            <Badge
                              variant={businessAccount.is_active ? "default" : "secondary"}
                              className="text-xs"
                            >
                              {businessAccount.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </div>

                          <div className="flex items-center">
                            {isSelected ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                            )}
                          </div>
                        </div>

                        {/* Permissions Row (Visible only if selected and role is AGENT) */}
                        {isSelected && formData.role === 'AGENT' && (
                          <div className="mt-3 pl-9 border-t pt-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`edit-perm-all-${businessAccount.id}`}
                                checked={hasAllAccess}
                                onCheckedChange={(checked) => handleValidationToggle(businessAccount.id, !!checked)}
                              />
                              <Label
                                htmlFor={`edit-perm-all-${businessAccount.id}`}
                                className="text-sm cursor-pointer"
                              >
                                View All Conversations
                              </Label>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 ml-6">
                              Can see and manage all conversations for this business account.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-6 text-center">
                  <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No business accounts found. Create a business account first.
                  </p>
                </CardContent>
              </Card>
            )}

            {selectedBusinessAccounts.length > 0 && (
              <div className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                {selectedBusinessAccounts.length} business account{selectedBusinessAccounts.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>
        </form>
      </ScrollableDialogContent>
    </Dialog>
  );
};
